import { type FC, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Badge, Input, NativeSelect } from '@/components/ui';
import { useAuth } from '@/hooks';
import { supabase } from '@/lib/api/supabase';
import { Package, Calendar, CreditCard, ChevronRight, Search, Filter, X } from 'lucide-react';
import type { Order, OrderItem, PaymentStatus, OrderStatus } from '@/types';
import { dbToOrder, dbToOrderItem } from '@/types';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';

interface OrderWithItems extends Order {
  items: OrderItem[];
}

/**
 * Order history page showing user's past orders
 * 
 * Requirements: 6.1 - Display list of all user orders
 */
export const OrderHistoryPage: FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [orderStatusFilter, setOrderStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'total-desc' | 'total-asc'>('date-desc');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  // Fetch user's orders
  useEffect(() => {
    const fetchOrders = async (): Promise<void> => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch orders with items
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select(`
            *,
            items:order_items(*)
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (ordersError) throw ordersError;

        // Convert database types to domain types
        const convertedOrders: OrderWithItems[] = (ordersData || []).map((orderData) => {
          const order = dbToOrder(orderData);
          const items = (orderData.items || []).map((item: any) => dbToOrderItem(item));
          return {
            ...order,
            items,
          };
        });

        setOrders(convertedOrders);
      } catch (err) {
        console.error('Failed to fetch orders:', err);
        setError(err instanceof Error ? err.message : 'Failed to load order history');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      void fetchOrders();
    }
  }, [user]);

  /**
   * Filter and sort orders based on search and filter criteria
   * Requirements: 6.1 - Add search by order number, filter by status, filter by date range, sorting options
   */
  const filteredAndSortedOrders = useMemo(() => {
    let filtered = [...orders];

    // Search by order number
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((order) =>
        order.orderNumber.toLowerCase().includes(query)
      );
    }

    // Filter by payment status
    if (paymentStatusFilter !== 'all') {
      filtered = filtered.filter((order) => order.paymentStatus === paymentStatusFilter);
    }

    // Filter by order status
    if (orderStatusFilter !== 'all') {
      filtered = filtered.filter((order) => order.orderStatus === orderStatusFilter);
    }

    // Filter by date range
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= fromDate;
      });
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.createdAt);
        return orderDate <= toDate;
      });
    }

    // Sort orders
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'date-asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'total-desc':
          return b.total - a.total;
        case 'total-asc':
          return a.total - b.total;
        default:
          return 0;
      }
    });

    return filtered;
  }, [orders, searchQuery, paymentStatusFilter, orderStatusFilter, dateFrom, dateTo, sortBy]);

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setSearchQuery('');
    setPaymentStatusFilter('all');
    setOrderStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortBy('date-desc');
  };

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = searchQuery || paymentStatusFilter !== 'all' || orderStatusFilter !== 'all' || dateFrom || dateTo || sortBy !== 'date-desc';

  /**
   * Get badge variant for payment status
   * Requirements: 6.5 - Indicate payment status clearly
   */
  const getPaymentStatusBadge = (status: PaymentStatus) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-500">Failed</Badge>;
      case 'refunded':
        return <Badge className="bg-gray-500">Refunded</Badge>;
      case 'partially_refunded':
        return <Badge className="bg-gray-500">Partially Refunded</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  /**
   * Get badge variant for order status
   * Requirements: 6.5 - Indicate order status clearly
   */
  const getOrderStatusBadge = (status: OrderStatus) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500">Processing</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  /**
   * Format price in cents to dollars
   */
  const formatPrice = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  /**
   * Get badge variant for fulfillment status
   * Requirements: 6.5 - Show fulfillment status for physical items
   */
  const getFulfillmentStatusBadge = (status: string) => {
    switch (status) {
      case 'delivered':
        return <Badge className="bg-green-500">Delivered</Badge>;
      case 'shipped':
        return <Badge className="bg-blue-500">Shipped</Badge>;
      case 'processing':
        return <Badge className="bg-yellow-500">Processing</Badge>;
      case 'pending':
        return <Badge className="bg-gray-500">Pending</Badge>;
      case 'pickup_ready':
        return <Badge className="bg-blue-500">Ready for Pickup</Badge>;
      case 'picked_up':
        return <Badge className="bg-green-500">Picked Up</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  /**
   * Check if order contains physical items that require fulfillment
   * Requirements: 6.5 - Show fulfillment status for physical items
   */
  const hasPhysicalItems = (order: OrderWithItems): boolean => {
    return order.items.some((item) => item.itemType === 'addon');
  };

  /**
   * Format date to readable string
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Loading order history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-red-600">
                <p className="font-medium">Error loading orders</p>
                <p className="text-sm mt-2">{error}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-viking text-primary mb-2">Order History</h1>
          <p className="text-muted-foreground">
            View your past orders and registration details
          </p>
        </div>

        {/* Search and Filters */}
        {orders.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle className="text-lg font-viking flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Search & Filter
                </CardTitle>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 self-start sm:self-auto"
                  >
                    <X className="h-4 w-4" />
                    Clear Filters
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Search by order number */}
                <div>
                  <label htmlFor="search" className="block text-sm font-medium mb-2">
                    Search by Order Number
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      type="text"
                      placeholder="Enter order number..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Filters row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Payment Status Filter */}
                  <div>
                    <label htmlFor="payment-status" className="block text-sm font-medium mb-2">
                      Payment Status
                    </label>
                    <NativeSelect
                      id="payment-status"
                      value={paymentStatusFilter}
                      onChange={(e) => setPaymentStatusFilter(e.target.value as PaymentStatus | 'all')}
                      options={[
                        { value: 'all', label: 'All Statuses' },
                        { value: 'completed', label: 'Paid' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'failed', label: 'Failed' },
                        { value: 'refunded', label: 'Refunded' },
                        { value: 'partially_refunded', label: 'Partially Refunded' },
                      ]}
                    />
                  </div>

                  {/* Order Status Filter */}
                  <div>
                    <label htmlFor="order-status" className="block text-sm font-medium mb-2">
                      Order Status
                    </label>
                    <NativeSelect
                      id="order-status"
                      value={orderStatusFilter}
                      onChange={(e) => setOrderStatusFilter(e.target.value as OrderStatus | 'all')}
                      options={[
                        { value: 'all', label: 'All Statuses' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'processing', label: 'Processing' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'cancelled', label: 'Cancelled' },
                      ]}
                    />
                  </div>

                  {/* Date From */}
                  <div>
                    <label htmlFor="date-from" className="block text-sm font-medium mb-2">
                      From Date
                    </label>
                    <Input
                      id="date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>

                  {/* Date To */}
                  <div>
                    <label htmlFor="date-to" className="block text-sm font-medium mb-2">
                      To Date
                    </label>
                    <Input
                      id="date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>

                {/* Sort Options */}
                <div>
                  <label htmlFor="sort-by" className="block text-sm font-medium mb-2">
                    Sort By
                  </label>
                  <NativeSelect
                    id="sort-by"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                    options={[
                      { value: 'date-desc', label: 'Date (Newest First)' },
                      { value: 'date-asc', label: 'Date (Oldest First)' },
                      { value: 'total-desc', label: 'Total (Highest First)' },
                      { value: 'total-asc', label: 'Total (Lowest First)' },
                    ]}
                    className="w-full sm:max-w-xs"
                  />
                </div>

                {/* Results count */}
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {filteredAndSortedOrders.length} of {orders.length} order{orders.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Orders List */}
        {orders.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No orders yet</h3>
                <p className="text-muted-foreground mb-6">
                  {REGISTRATIONS_CLOSED
                    ? "You haven't placed any orders."
                    : "You haven't placed any orders. Start by registering for the event!"}
                </p>
                {!REGISTRATIONS_CLOSED && (
                  <button
                    onClick={() => navigate('/event-registration')}
                    className="bg-primary text-primary-foreground px-6 py-2 rounded-md hover:bg-primary/90"
                  >
                    Register Now
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : filteredAndSortedOrders.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No orders found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your search or filter criteria
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="bg-primary text-primary-foreground px-6 py-2 rounded-md hover:bg-primary/90"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredAndSortedOrders.map((order) => (
              <Card
                key={order.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-viking mb-2">
                        Order #{order.orderNumber}
                      </CardTitle>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(order.createdAt)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Package className="h-4 w-4" />
                          <span>{order.items.length} item{order.items.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                      <div className="text-xl font-bold">{formatPrice(order.total)}</div>
                      <div className="flex gap-2 flex-wrap justify-end">
                        {getPaymentStatusBadge(order.paymentStatus)}
                        {getOrderStatusBadge(order.orderStatus)}
                        {hasPhysicalItems(order) && getFulfillmentStatusBadge(order.fulfillmentStatus)}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Order Items Summary */}
                  <div className="space-y-2">
                    {order.items.slice(0, 3).map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-primary"></div>
                          <span className="font-medium">{item.itemName}</span>
                          {item.quantity > 1 && (
                            <span className="text-muted-foreground">x{item.quantity}</span>
                          )}
                        </div>
                        <span className="text-muted-foreground">
                          {formatPrice(item.total)}
                        </span>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <div className="text-sm text-muted-foreground pl-4">
                        +{order.items.length - 3} more item{order.items.length - 3 !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  {/* View Details Link */}
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {order.paidAt && (
                        <div className="flex items-center gap-1">
                          <CreditCard className="h-4 w-4" />
                          <span>Paid on {formatDate(order.paidAt)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-primary font-medium text-sm">
                      <span>View Details</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {orders.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl font-viking">Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Orders</p>
                  <p className="text-xl sm:text-2xl font-bold">{orders.length}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                  <p className="text-xl sm:text-2xl font-bold">
                    {formatPrice(
                      orders
                        .filter((o) => o.paymentStatus === 'completed')
                        .reduce((sum, o) => sum + o.total, 0)
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Completed Orders</p>
                  <p className="text-xl sm:text-2xl font-bold">
                    {orders.filter((o) => o.orderStatus === 'completed').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
